import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { CircleUserRound, Crosshair, Dices, Flag, Hourglass, Layers, Menu, PlugZap, ScrollText, Settings, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ACTIONS, MODES, applyAction, getContextualMoves, getLegalMoves } from '../engine';
import { gameReducer } from '../state/gameReducer';
import { absorbChoose, absorbSkip, newGame, startRound } from '../state/actions';
import type { Action, Card, GameState } from '../engine';
import type { MoveOption } from '../engine/moves';
import { chooseAiAction, type AiDifficulty } from '../ai/chooseAiAction';
import { finalizeAiLearningGame, getLocalAiProfile, recordAiMove, resetAiLearningSession, sanitizeAiLearningProfile, setLocalAiProfile } from '../ai/aiLearning';
import SettingsModal, { loadGameSettings, type GameSettings } from '../components/SettingsModal';
import ConfirmModal from '../components/ConfirmModal';
import { appendTrainingMove, getTrainingGame, upsertAiProfile, upsertTrainingGame } from '../training/trainingQueue';
import type { TrainingAiProfileRecord, TrainingGameRecord, TrainingMoveRecord } from '../training/trainingTypes';
import {
  loadActiveProfileState,
  saveActiveProfileState,
  getActiveUserProfileName,
} from '../profiles/userProfiles';
import { uuidV4 } from '../utils/uuid';
import './EngineGame.css';

function suitSymbol(suit: Card['suit']): string {
  if (suit === 'S') return '♠';
  if (suit === 'H') return '♥';
  if (suit === 'D') return '♦';
  return '♣';
}

function isRed(card: Card): boolean {
  return card.suit === 'H' || card.suit === 'D';
}

function renderCard(
  card: Card,
  extraClass = '',
  onClick?: (event: React.MouseEvent) => void,
  style?: React.CSSProperties,
  overlay?: React.ReactNode
): JSX.Element {
  const content = (
    <>
      <span>{card.rank}</span>
      <span>{suitSymbol(card.suit)}</span>
    </>
  );
  if (!onClick) {
    return (
      <motion.span
        key={card.id}
        layout
        initial={{ opacity: 0, y: -14, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 14, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className={`engine-card ${isRed(card) ? 'red' : ''} ${extraClass}`.trim()}
        style={style}
      >
        {content}
        {overlay}
      </motion.span>
    );
  }
  return (
    <motion.button
      key={card.id}
      layout
      initial={{ opacity: 0, y: -14, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 14, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.97 }}
      className={`engine-card ${isRed(card) ? 'red' : ''} ${extraClass}`.trim()}
      title={compactCard(card)}
      onClick={onClick}
      style={style}
    >
      {content}
      {overlay}
    </motion.button>
  );
}

function renderStaticCard(card: Card, extraClass = ''): JSX.Element {
  return (
    <motion.span
      layout
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.16 }}
      className={`engine-card ${isRed(card) ? 'red' : ''} ${extraClass}`.trim()}
    >
      <span>{card.rank}</span>
      <span>{suitSymbol(card.suit)}</span>
    </motion.span>
  );
}

function compactCard(card: Card): string {
  return `${card.rank}${suitSymbol(card.suit)}`;
}

function fmtHint(lines: Array<string | null | undefined>): string {
  return lines.filter((v): v is string => Boolean(v && v.trim().length)).join('\n');
}

function shortMoveError(raw: string): string {
  const msg = (raw || '').trim();
  if (!msg) return 'NOT ALLOWED';
  const m = msg.toLowerCase();
  if (m.includes('closed build') && m.includes('tempered')) return 'BUILD CLOSED';
  if (m.includes('pending royals')) return 'PENDING ROYALS';
  if (m.includes('resolve pending absorption')) return 'ABSORB FIRST';
  if (m.includes('not this player turn')) return 'NOT YOUR TURN';
  if (m.includes('can only reinforce your own')) return 'NOT YOUR PILE';
  if (m.includes('can only add to your own team')) return 'NOT YOUR TEAM';
  if (m.includes('cannot update your own build')) return 'OWN BUILD';
  if (m.includes('must keep one matching value card')) return 'KEEP MATCH';
  if (m.includes('card must match') && m.includes('reinforce')) return 'NEED MATCH';
  if (m.includes('sum') && m.includes('equal')) return 'SUM MISMATCH';
  if (m.includes('must total')) return 'SUM MISMATCH';
  if (m.includes('numeric table cards')) return 'NUMBERS ONLY';
  if (m.includes('royals cannot')) return 'ROYAL NOT ALLOWED';
  if (m.includes('opponent collection is empty')) return 'OPP EMPTY';
  if (m.includes('opponent collection top is not numeric')) return 'OPP TOP ROYAL';
  if (m.includes('top changed')) return 'STATE CHANGED';
  if (m.includes('pile not found')) return 'PILE MISSING';
  return 'NOT ALLOWED';
}

function renderPileStack(cards: Card[], prefix: string): JSX.Element {
  if (!cards.length) {
    return <span className="engine-card back pile-stack-card">?</span>;
  }
  if (cards.length === 1) {
    return <span className="pile-stack-preview">{renderStaticCard(cards[0], 'pile-stack-card')}</span>;
  }

  const maxLayers = Math.min(3, cards.length);
  const previewCards = cards.slice(-maxLayers);
  const layerCount = previewCards.length;

  const density = Math.min(1, Math.max(0, (cards.length - 2) / 14));
  const dx = Math.max(1, Math.round(2 + (1 - density) * 4)); // 6 -> 2
  const dy = Math.max(1, Math.round(1 + (1 - density) * 2)); // 3 -> 1

  const stackWidth = 42 + Math.max(0, layerCount - 1) * dx;
  const stackHeight = 60 + Math.max(0, layerCount - 1) * dy;
  return (
    <div className="pile-stack-preview" style={{ width: `${stackWidth}px`, minHeight: `${stackHeight}px` }}>
      {previewCards.map((card, idx) => (
        <motion.span
          key={`${prefix}-${card.id}`}
          layout
          className="pile-stack-layer"
          style={{ transform: `translate(${idx * dx}px, ${idx * dy}px)` }}
        >
          {idx < layerCount - 1 ? (
            <span className="engine-card pile-stack-card pile-stack-blank" aria-hidden="true" />
          ) : (
            renderStaticCard(card, 'pile-stack-card')
          )}
        </motion.span>
      ))}
    </div>
  );
}

function dedupeMoves(moves: MoveOption[]): MoveOption[] {
  const map = new Map<string, MoveOption>();
  moves.forEach((move) => {
    const key = JSON.stringify(move);
    if (!map.has(key)) map.set(key, move);
  });
  return Array.from(map.values());
}

interface EngineGameProps {
  stateOverride?: GameState;
  onDispatchAction?: (action: Action) => void;
  viewerPlayerId?: string;
  lockedMode?: GameState['mode'];
  disableLocalControls?: boolean;
  onHudChange?: (hud: EngineHudState) => void;
  playMode?: 'LOCAL' | 'ONLINE';
  onPlayModeChange?: (mode: 'LOCAL' | 'ONLINE') => void;
  inviteCode?: string;
  onlineSeatLabel?: string;
  onlineTeamLabel?: string;
  onlineStatusLabel?: string;
  onLeaveOnline?: () => void;
  onNewGameRequest?: () => void;
  showReconnect?: boolean;
  onReconnect?: () => void;
  difficulty?: 'EASY' | 'NORMAL' | 'HARD';
  aiEnabled?: boolean;
  aiPlayerIds?: string[];
  onQuitRequest?: () => void;
}

export interface EngineHudState {
  round: number;
  currentPlayerId: string;
  viewerPlayerId: string;
  deckCount: number;
  phase: GameState['phase'];
  mode: GameState['mode'];
  isSetup: boolean;
}

export interface EngineGameHandle {
  newGame: () => void;
  startRound: () => void;
  clearSelection: () => void;
  setMode: (nextMode: GameState['mode']) => void;
}

const EngineGame = forwardRef<EngineGameHandle, EngineGameProps>(function EngineGame({
    stateOverride,
    onDispatchAction,
    viewerPlayerId: viewerPlayerIdProp,
    lockedMode,
    disableLocalControls,
    onHudChange,
    playMode = 'LOCAL',
    onPlayModeChange,
    inviteCode,
    onlineSeatLabel,
    onlineTeamLabel,
    onlineStatusLabel,
    onLeaveOnline,
    onNewGameRequest,
    showReconnect,
    onReconnect,
    difficulty,
    aiEnabled,
    aiPlayerIds,
    onQuitRequest,
  }: EngineGameProps,
  ref
	): JSX.Element {
	  const stageRef = useRef<HTMLDivElement | null>(null);
	  const stageContentRef = useRef<HTMLDivElement | null>(null);
	  const topbarRef = useRef<HTMLElement | null>(null);
	  const movePopupRef = useRef<HTMLDivElement | null>(null);
    const aiProfileFileRef = useRef<HTMLInputElement | null>(null);
  const [mode, setMode] = useState<GameState['mode']>(lockedMode || MODES.TWO_PLAYER);
  const [error, setError] = useState('');
  const [selectedHandCardId, setSelectedHandCardId] = useState<string | null>(null);
  const [selectedHandGhostPos, setSelectedHandGhostPos] = useState<{ x: number; y: number } | null>(null);
  const [selectedTableLooseIds, setSelectedTableLooseIds] = useState<Set<string>>(new Set());
  const [selectedBuildIds, setSelectedBuildIds] = useState<Set<string>>(new Set());
  const [selectedStayIds, setSelectedStayIds] = useState<Set<string>>(new Set());
  const [selectedOpponentTopTeamId, setSelectedOpponentTopTeamId] = useState<string | null>(null);
  const [selectedPendingRoyalOwnerId, setSelectedPendingRoyalOwnerId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [restartOpen, setRestartOpen] = useState<boolean>(false);
  const [logOpen, setLogOpen] = useState<boolean>(false);
  const [scoreSummaryOpen, setScoreSummaryOpen] = useState<boolean>(false);
  const [movePopup, setMovePopup] = useState<{ x: number; y: number; moves: Action[] } | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [errorToast, setErrorToast] = useState<{ message: string; x: number; y: number } | null>(null);
  const [pileHint, setPileHint] = useState<{
    id: string;
    lines: string[];
    anchor: { left: number; right: number; top: number; bottom: number; width: number; height: number };
  } | null>(null);
  const [pileHintLayout, setPileHintLayout] = useState<{ placement: 'top' | 'bottom'; pos: { left: number; top: number } } | null>(null);
  const [gameSettings, setGameSettings] = useState<GameSettings>(() => loadGameSettings());
  const [aiTakeover, setAiTakeover] = useState<boolean>(false);
  // Fullscreen toggle removed (mobile browsers often handle fullscreen inconsistently).
  const [localState, localDispatch] = useReducer(gameReducer, undefined, () =>
    (() => {
      const restored = loadActiveProfileState();
      if (restored) return restored;
      const base = gameReducer(undefined, newGame({ mode: lockedMode || MODES.TWO_PLAYER, seed: `${Date.now()}-${Math.random()}` }));
      return gameReducer(base, startRound(base.meta.roundIndex));
    })()
  );
  const state = stateOverride || localState;
  const aiDifficulty: AiDifficulty = (difficulty || 'NORMAL') as AiDifficulty;
  const configuredAiPlayers = useMemo(() => new Set(aiPlayerIds && aiPlayerIds.length ? aiPlayerIds : ['P2']), [aiPlayerIds]);
  const trainingEnabled = !stateOverride && !disableLocalControls && typeof indexedDB !== 'undefined';
  const trainingGameIdRef = useRef<string | null>(null);
  const trainingTurnIndexRef = useRef<number>(0);
  const turnStartMsRef = useRef<number>(Date.now());
  const aiThinkDelayMsRef = useRef<Record<string, number>>({});
  const autoStartAfterNewGameRef = useRef<boolean>(false);
  const handGhostRafRef = useRef<number | null>(null);
  const handGhostLastPosRef = useRef<{ x: number; y: number } | null>(null);
  const pileHintTimerRef = useRef<number | null>(null);
  const pileHintAutoCloseTimerRef = useRef<number | null>(null);
  const suppressPileClickUntilRef = useRef<number>(0);
  const pileHintRef = useRef<HTMLDivElement | null>(null);
  const errorToastTimerRef = useRef<number | null>(null);

  const showErrorToast = useCallback((message: string, x: number, y: number) => {
    const margin = 8;
    const clampedX = Math.max(margin, Math.min(window.innerWidth - margin, x));
    const clampedY = Math.max(margin, Math.min(window.innerHeight - margin, y));
    setErrorToast({ message, x: clampedX, y: clampedY });
    if (errorToastTimerRef.current) window.clearTimeout(errorToastTimerRef.current);
    errorToastTimerRef.current = window.setTimeout(() => setErrorToast(null), 1200);
  }, []);

  const cancelPileHintTimer = useCallback(() => {
    if (pileHintTimerRef.current) {
      window.clearTimeout(pileHintTimerRef.current);
      pileHintTimerRef.current = null;
    }
  }, []);

  const cancelPileHintAutoCloseTimer = useCallback(() => {
    if (pileHintAutoCloseTimerRef.current) {
      window.clearTimeout(pileHintAutoCloseTimerRef.current);
      pileHintAutoCloseTimerRef.current = null;
    }
  }, []);

  const schedulePileHintAutoClose = useCallback(
    (id: string, timeoutMs: number) => {
      cancelPileHintAutoCloseTimer();
      pileHintAutoCloseTimerRef.current = window.setTimeout(() => {
        setPileHint((prev) => {
          if (prev?.id !== id) return prev;
          setPileHintLayout(null);
          return null;
        });
      }, timeoutMs);
    },
    [cancelPileHintAutoCloseTimer]
  );

  const onPilePointerDown = useCallback(
    (pileId: string, hintText: string) => (event: React.PointerEvent) => {
      if (event.pointerType !== 'touch') return;
      cancelPileHintTimer();
      const target = event.currentTarget as HTMLElement;
      pileHintTimerRef.current = window.setTimeout(() => {
        const rect = target.getBoundingClientRect();
        setPileHint({
          id: pileId,
          lines: hintText.split('\n'),
          anchor: { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
        setPileHintLayout(null);
        schedulePileHintAutoClose(pileId, 2600);
        suppressPileClickUntilRef.current = Date.now() + 800;
      }, 450);
    },
    [cancelPileHintTimer, schedulePileHintAutoClose]
  );

  const onPilePointerUp = useCallback(() => {
    cancelPileHintTimer();
    cancelPileHintAutoCloseTimer();
    setPileHint(null);
    setPileHintLayout(null);
  }, [cancelPileHintTimer, cancelPileHintAutoCloseTimer]);

  const openPileHintFromEvent = useCallback((pileId: string, hintText: string) => (event: React.SyntheticEvent) => {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setPileHint({
      id: pileId,
      lines: hintText.split('\n'),
      anchor: { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom, width: rect.width, height: rect.height },
    });
    setPileHintLayout(null);
    schedulePileHintAutoClose(pileId, 2600);
  }, [schedulePileHintAutoClose]);

  const closePileHint = useCallback(() => {
    cancelPileHintAutoCloseTimer();
    setPileHint(null);
    setPileHintLayout(null);
  }, [cancelPileHintAutoCloseTimer]);

  const shouldSuppressPileClick = useCallback((event: React.SyntheticEvent) => {
    if (Date.now() >= suppressPileClickUntilRef.current) return false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  }, []);

  useEffect(() => {
    if (!pileHint) return;
    const onScroll = () => {
      setPileHint(null);
      setPileHintLayout(null);
    };
    const onResize = () => {
      setPileHint(null);
      setPileHintLayout(null);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [pileHint]);

  useLayoutEffect(() => {
    if (!pileHint) return;
    const hintEl = pileHintRef.current;
    if (!hintEl) return;
    const gap = 8;
    const margin = 8;
    const width = hintEl.offsetWidth || 200;
    const height = hintEl.offsetHeight || 0;
    const centerX = pileHint.anchor.left + pileHint.anchor.width / 2;
    const left = Math.min(Math.max(centerX, margin + width / 2), window.innerWidth - margin - width / 2);
    const canShowBelow = pileHint.anchor.bottom + gap + height <= window.innerHeight - margin;
    const placement: 'top' | 'bottom' = canShowBelow ? 'bottom' : 'top';
    const top =
      placement === 'bottom'
        ? Math.min(pileHint.anchor.bottom + gap, window.innerHeight - margin - height)
        : Math.max(margin, pileHint.anchor.top - gap - height);
    setPileHintLayout({ placement, pos: { left, top } });
  }, [pileHint]);

  const cancelPickedUpHandCard = useCallback(() => {
    setSelectedHandCardId(null);
    setSelectedHandGhostPos(null);
    setMovePopup(null);
  }, []);

  const downloadAiProfileJson = useCallback(() => {
    const profile = getLocalAiProfile();
    const json = JSON.stringify(profile, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omulongo_ai_profile_v${profile.version}_${profile.updatedAt.slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const triggerAiProfileUpload = useCallback(() => {
    aiProfileFileRef.current?.click();
  }, []);

  const onAiProfileFileChange = useCallback(async () => {
    const input = aiProfileFileRef.current;
    const file = input?.files?.[0] || null;
    if (!file) return;
    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw) as unknown;
      const sanitized = sanitizeAiLearningProfile(parsed);
      if (!sanitized) throw new Error('Invalid profile JSON.');
      setLocalAiProfile(sanitized);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI profile JSON.');
    } finally {
      if (input) input.value = '';
    }
  }, []);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const content = stageContentRef.current;
    if (!stage || !content) return;
    // Scroll layout: keep UI scale fixed so restart does not "resize" the table.
    stage.style.setProperty('--ui-scale', '1');

    // Topbar is fixed and can wrap; measure its height and add enough top padding so content never hides behind it.
    const topbar = topbarRef.current;
    const updateTopbarOffset = () => {
      if (!topbar) return;
      const h = topbar.offsetHeight || 0;
      stage.style.setProperty('--topbar-offset', `${h + 12}px`);
    };
    updateTopbarOffset();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && topbar) {
      ro = new ResizeObserver(() => updateTopbarOffset());
      ro.observe(topbar);
    }
    window.addEventListener('resize', updateTopbarOffset);

    return () => {
      window.removeEventListener('resize', updateTopbarOffset);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (stateOverride || disableLocalControls) return;
    if (playMode !== 'LOCAL') return;
    // Auto-save local state (especially for AI games) so players can resume.
    const t = window.setTimeout(() => {
      saveActiveProfileState(state);
    }, 250);
    return () => window.clearTimeout(t);
  }, [state, stateOverride, disableLocalControls, playMode]);

  useEffect(() => {
    if (!movePopup) return;
    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (movePopupRef.current && movePopupRef.current.contains(target)) return;
      setMovePopup(null);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [movePopup]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [menuOpen]);

  // Fullscreen removed.
  const aiActive = (Boolean(aiEnabled) || aiTakeover) && !stateOverride && !disableLocalControls;
  const aiPlayers = useMemo(() => {
    const base = new Set(configuredAiPlayers);
    if (aiTakeover) {
      for (const p of state.players) base.add(p.id);
    }
    return base;
  }, [configuredAiPlayers, aiTakeover, state.players]);
  const isSelfPlay = aiPlayers.has('P1') && aiPlayers.has('P2') && aiTakeover;
  const isAiVsAi = aiActive && state.players.length > 0 && state.players.every((p) => aiPlayers.has(p.id));
  const viewerPlayerId = viewerPlayerIdProp || (isAiVsAi ? (state.players[0]?.id || state.currentPlayerId) : state.currentPlayerId);
  const aiThinking = aiActive && state.phase === 'playing' && aiPlayers.has(state.currentPlayerId);
  const effectiveDeckRemaining = state.deck.length + (state.meta.reservedRound2Cards?.length || 0);
  const teamOrder = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const player of state.players) {
      if (seen.has(player.teamId)) continue;
      seen.add(player.teamId);
      ordered.push(player.teamId);
    }
    return ordered;
  }, [state.players]);

  const activeProfileName = useMemo(() => {
    if (stateOverride) return null;
    return getActiveUserProfileName();
  }, [stateOverride]);

  const playerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const player of state.players) map[player.id] = player.id;
    if (activeProfileName && map.P1) map.P1 = activeProfileName;
    return map;
  }, [state.players, activeProfileName]);

  const canonicalPlayerLabel = useCallback(
    (playerId: string): string => {
      return playerNameById[playerId] || playerId;
    },
    [playerNameById]
  );

  const teamLabelById = useMemo(() => {
    const playerIdOrder = state.players.map((p) => p.id);
    const playerLabel = (playerId: string): string => {
      if (playerNameById[playerId]) return playerNameById[playerId];
      const direct = /^P(\d+)$/.exec(playerId);
      if (direct) return `P${direct[1]}`;
      const idx = playerIdOrder.indexOf(playerId);
      return `P${idx >= 0 ? idx + 1 : playerId}`;
    };

    const labels: Record<string, string> = {};
    teamOrder.forEach((teamId, idx) => {
      const members = state.players.filter((p) => p.teamId === teamId).map((p) => p.id);
      if (state.mode === '2p' && members.length === 1) {
        labels[teamId] = playerLabel(members[0]);
        return;
      }
      if (members.length === 1) {
        labels[teamId] = playerLabel(members[0]);
        return;
      }
      labels[teamId] = `Team ${idx + 1} (${members.map(playerLabel).join(' + ')})`;
    });
    return labels;
  }, [state.players, state.mode, teamOrder, playerNameById]);

  const hasTableSelection =
    selectedTableLooseIds.size > 0 || selectedBuildIds.size > 0 || selectedStayIds.size > 0 || Boolean(selectedOpponentTopTeamId);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (movePopup) {
        setMovePopup(null);
        return;
      }
      if (selectedHandCardId || hasTableSelection || selectedPendingRoyalOwnerId) {
        cancelPickedUpHandCard();
        setSelectedPendingRoyalOwnerId(null);
        setSelectedTableLooseIds(new Set());
        setSelectedBuildIds(new Set());
        setSelectedStayIds(new Set());
        setSelectedOpponentTopTeamId(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [movePopup, selectedHandCardId, hasTableSelection, selectedPendingRoyalOwnerId, cancelPickedUpHandCard]);

  useEffect(() => {
    if (!selectedHandCardId) {
      setSelectedHandGhostPos(null);
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      handGhostLastPosRef.current = { x: event.clientX, y: event.clientY };
      if (handGhostRafRef.current !== null) return;
      handGhostRafRef.current = window.requestAnimationFrame(() => {
        handGhostRafRef.current = null;
        if (handGhostLastPosRef.current) setSelectedHandGhostPos(handGhostLastPosRef.current);
      });
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (handGhostRafRef.current !== null) {
        window.cancelAnimationFrame(handGhostRafRef.current);
        handGhostRafRef.current = null;
      }
    };
  }, [selectedHandCardId]);

  const safeDispatch = useCallback((action: ReturnType<typeof newGame> | ReturnType<typeof startRound> | ReturnType<typeof absorbChoose> | ReturnType<typeof absorbSkip> | Action) => {
    try {
      setError('');
      if (action.type === ACTIONS.NEW_GAME) {
        resetAiLearningSession();
        setAiTakeover(false);
      }
      if (trainingEnabled) {
        if (action.type === ACTIONS.NEW_GAME) {
          const id = uuidV4();
          trainingGameIdRef.current = id;
          trainingTurnIndexRef.current = 0;
          aiThinkDelayMsRef.current = {};
          const record: TrainingGameRecord = {
            id,
            source: 'client',
            mode: action.mode,
            seed: (action as any).seed ?? null,
            status: 'INCOMPLETE',
            aiSettings: {
              difficulty: aiDifficulty,
              aiEnabled: aiEnabled ?? false,
              aiPlayerIds: Array.from(aiPlayers),
              thinkDelayMinMs: 1000,
            },
            startedAt: new Date().toISOString(),
            moves: [],
            syncedAt: null,
            updatedAt: new Date().toISOString(),
          };
          upsertTrainingGame(record).catch(() => {});
        }

        const gameId = trainingGameIdRef.current;
        if (gameId) {
          const turnIndex = trainingTurnIndexRef.current;
          trainingTurnIndexRef.current += 1;
          const playerId: string = (action as any).playerId || state.currentPlayerId;
          const teamId = state.players.find((p) => p.id === playerId)?.teamId;
          const seat = /^P(\d+)$/.test(playerId) ? Number(playerId.slice(1)) : undefined;
          const legalMovesCount =
            state.phase === 'playing' && state.currentPlayerId === playerId ? getLegalMoves(state, playerId).length : undefined;
          const elapsedMs = state.phase === 'playing' ? Math.max(0, Date.now() - turnStartMsRef.current) : undefined;
          const thinkDelayMs = aiThinkDelayMsRef.current[playerId];
          const moveRecord: TrainingMoveRecord = {
            id: uuidV4(),
            turnIndex,
            roundIndex: state.meta.roundIndex,
            playerId,
            teamId,
            seat,
            move: action,
            legalMovesCount,
            elapsedMs,
            thinkDelayMs,
            createdAt: new Date().toISOString(),
          };
          appendTrainingMove(gameId, moveRecord).catch(() => {});
          delete aiThinkDelayMsRef.current[playerId];
        }
      }
      if (onDispatchAction) {
        onDispatchAction(action);
      } else {
        localDispatch(action);
      }
      cancelPickedUpHandCard();
      setSelectedTableLooseIds(new Set());
      setSelectedBuildIds(new Set());
      setSelectedStayIds(new Set());
      setSelectedOpponentTopTeamId(null);
      setSelectedPendingRoyalOwnerId(null);
      setMovePopup(null);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Action failed.';
      setError(raw);
      showErrorToast(shortMoveError(raw), window.innerWidth / 2, 86);
    }
  }, [onDispatchAction, trainingEnabled, state, aiDifficulty, aiEnabled, aiPlayers, cancelPickedUpHandCard, showErrorToast]);

  useEffect(() => {
    if (!aiThinking) return;
    const minThinkMs = gameSettings.gameplay.aiThinkMinMs ?? 1000;
    const jitterMs = Math.floor(Math.random() * 250);
    const thinkDelay = minThinkMs + jitterMs;
    aiThinkDelayMsRef.current[state.currentPlayerId] = thinkDelay;
    const handle = window.setTimeout(() => {
      try {
        const action = chooseAiAction(state, state.currentPlayerId, aiDifficulty, undefined, { perfectInfo: playMode === 'LOCAL' });
        if (aiDifficulty === 'HARD' && !isSelfPlay) {
          recordAiMove(state, action, state.currentPlayerId);
        }
        safeDispatch(action);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'AI action failed.');
      }
    }, thinkDelay);
    return () => window.clearTimeout(handle);
  }, [aiThinking, state, aiDifficulty, safeDispatch, gameSettings.gameplay.aiThinkMinMs, isSelfPlay, playMode]);

  useEffect(() => {
    if (stateOverride || disableLocalControls) return;
    if (!gameSettings.keyboard.enabled) return;

    const isEditableTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || (el as any).isContentEditable;
    };

    const isMac = typeof navigator !== 'undefined' ? /mac/i.test(navigator.platform || '') : false;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;

      const wantsMac = isMac && event.metaKey;
      const wantsWin = !isMac && event.ctrlKey;
      const wantsFallback = event.metaKey && event.ctrlKey; // also allow Ctrl+Cmd+.
      const wantsMod = wantsMac || wantsWin || wantsFallback;
      if (!wantsMod) return;
      if (event.shiftKey || event.altKey) return;

      if ((event.key === '\\' || event.code === 'Backslash') && playMode === 'LOCAL') {
        event.preventDefault();
        event.stopPropagation();
        onPlayModeChange?.('ONLINE');
        return;
      }

      if (event.key === ',' || event.code === 'Comma') {
        event.preventDefault();
        event.stopPropagation();
        triggerAiProfileUpload();
        return;
      }

      if (event.key === '/' || event.key === '?' || event.code === 'Slash') {
        event.preventDefault();
        event.stopPropagation();
        downloadAiProfileJson();
        return;
      }

      if (playMode !== 'LOCAL') return;
      if (event.key !== '.') return;

      event.preventDefault();
      event.stopPropagation();
      setAiTakeover((prev) => !prev);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [playMode, stateOverride, disableLocalControls, gameSettings.keyboard.enabled, triggerAiProfileUpload, downloadAiProfileJson, onPlayModeChange]);

  useEffect(() => {
    if (!trainingEnabled) return;
    if (state.phase !== 'playing') return;
    turnStartMsRef.current = Date.now();
  }, [trainingEnabled, state.phase, state.currentPlayerId, state.meta.roundIndex]);

  useEffect(() => {
    if (!trainingEnabled) return;
    if (trainingGameIdRef.current) return;

    const id = uuidV4();
    trainingGameIdRef.current = id;
    trainingTurnIndexRef.current = 0;
    const record: TrainingGameRecord = {
      id,
      source: 'client',
      mode: state.mode,
      seed: state.meta.seed,
      status: 'INCOMPLETE',
      aiSettings: {
        difficulty: aiDifficulty,
        aiEnabled: aiEnabled ?? false,
        aiPlayerIds: Array.from(aiPlayers),
        thinkDelayMinMs: 1000,
      },
      startedAt: new Date().toISOString(),
      moves: [],
      syncedAt: null,
      updatedAt: new Date().toISOString(),
    };
    upsertTrainingGame(record).catch(() => {});
  }, [trainingEnabled, state.mode, state.meta.seed, aiDifficulty, aiEnabled, aiPlayers]);

  useEffect(() => {
    if (!trainingEnabled) return;
    if (state.phase !== 'gameOver') return;
    const id = trainingGameIdRef.current;
    if (!id) return;
    getTrainingGame(id)
      .then((existing) => {
        if (!existing || existing.status === 'COMPLETED') return;
        const scores = state.meta.scores || {};
        const entries = Object.entries(scores).map(([teamId, s]) => ({ teamId, total: (s as any)?.total ?? 0 }));
        entries.sort((a, b) => b.total - a.total);
        const winner = entries.length ? entries[0] : null;
        const runnerUp = entries.length > 1 ? entries[1] : null;
        const winnerTeamId = winner && (!runnerUp || winner.total > runnerUp.total) ? winner.teamId : null;
        const loserTeamId = winnerTeamId && runnerUp ? runnerUp.teamId : null;

        const completed: TrainingGameRecord = {
          ...existing,
          status: 'COMPLETED',
          seed: state.meta.seed,
          finalScores: scores,
          winnerTeamId,
          loserTeamId,
          completedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return upsertTrainingGame(completed);
      })
      .catch(() => {});
  }, [trainingEnabled, state.phase, state.meta.scores, state.meta.seed]);

  useEffect(() => {
    if (!aiActive) return;
    if (aiDifficulty !== 'HARD') return;
    if (state.phase !== 'gameOver') return;
    if (isSelfPlay) return;

    const scores = state.meta.scores || {};
    const entries = Object.entries(scores).map(([teamId, s]) => ({ teamId, total: (s as any)?.total ?? 0 }));
    entries.sort((a, b) => b.total - a.total);
    const winner = entries.length ? entries[0] : null;
    const runnerUp = entries.length > 1 ? entries[1] : null;
    const winnerTeamId = winner && (!runnerUp || winner.total > runnerUp.total) ? winner.teamId : null;

    const aiPlayerId = Array.from(aiPlayers).find((pid) => state.players.some((p) => p.id === pid)) || null;
    const aiTeamId = aiPlayerId ? state.players.find((p) => p.id === aiPlayerId)?.teamId ?? null : null;

    const profile = finalizeAiLearningGame({ winnerTeamId, aiTeamId });

    if (trainingEnabled) {
      const record: TrainingAiProfileRecord = {
        id: uuidV4(),
        source: 'client',
        version: profile.version,
        weights: profile.weights,
        createdAt: profile.updatedAt,
        syncedAt: null,
      };
      upsertAiProfile(record).catch(() => {});
    }
  }, [aiActive, aiDifficulty, state.phase, state.meta.scores, state.players, aiPlayers, trainingEnabled, isSelfPlay]);

  useEffect(() => {
    if (!autoStartAfterNewGameRef.current) return;
    if (state.phase !== 'betweenRounds') return;
    autoStartAfterNewGameRef.current = false;
    safeDispatch(startRound(state.meta.roundIndex));
  }, [state.phase, state.meta.roundIndex, safeDispatch]);

  useEffect(() => {
    setScoreSummaryOpen(state.phase === 'gameOver');
  }, [state.phase, playMode]);

  const computeRestartStarter = useCallback((prevState: GameState): string => {
    const scores = prevState.meta.scores || {};
    const entries = Object.entries(scores)
      .map(([teamId, breakdown]) => ({ teamId, total: typeof (breakdown as any)?.total === 'number' ? (breakdown as any).total : 0 }))
      .filter((e) => typeof e.total === 'number');

    if (prevState.phase === 'gameOver' && entries.length) {
      const minTotal = Math.min(...entries.map((e) => e.total));
      const losingTeams = entries.filter((e) => e.total === minTotal).map((e) => e.teamId);
      if (losingTeams.length === 1) {
        const teamId = losingTeams[0];
        const memberIds = prevState.players.filter((p) => p.teamId === teamId).map((p) => p.id).sort();
        if (memberIds.length) return memberIds[0];
      }
    }

    const idx = prevState.players.findIndex((p) => p.id === prevState.currentPlayerId);
    if (idx < 0 || prevState.players.length <= 1) return prevState.players[0]?.id || 'P1';
    return prevState.players[(idx + 1) % prevState.players.length].id;
  }, []);

  const restartFromScoreSummary = useCallback(() => {
    if (playMode !== 'LOCAL') return;
    if (stateOverride || disableLocalControls) return;
    setScoreSummaryOpen(false);
    autoStartAfterNewGameRef.current = true;
    const modeToUse = lockedMode || state.mode;
    const restartStarter = computeRestartStarter(state);
    safeDispatch(newGame({ mode: modeToUse, seed: `${Date.now()}-${Math.random()}`, starterPlayerId: restartStarter }));
  }, [playMode, stateOverride, disableLocalControls, lockedMode, safeDispatch, state, computeRestartStarter]);

  const restartFromScoreSummaryOnline = useCallback(() => {
    if (playMode !== 'ONLINE') return;
    setScoreSummaryOpen(false);
    onNewGameRequest?.();
  }, [playMode, onNewGameRequest]);

  const quitFromScoreSummary = useCallback(() => {
    setScoreSummaryOpen(false);
    if (playMode === 'ONLINE') {
      onLeaveOnline?.();
      return;
    }
    onQuitRequest?.();
  }, [playMode, onLeaveOnline, onQuitRequest]);

  const restartLocalGame = useCallback(() => {
    if (stateOverride || disableLocalControls) return;
    autoStartAfterNewGameRef.current = true;
    const restartStarter = computeRestartStarter(state);
    safeDispatch(newGame({ mode: lockedMode || mode, seed: `${Date.now()}-${Math.random()}`, starterPlayerId: restartStarter }));
  }, [stateOverride, disableLocalControls, lockedMode, mode, safeDispatch, state, computeRestartStarter]);

  const computeContextMoves = useCallback(
    (targets: { looseCardIds: string[]; buildPileIds: string[]; stayPileIds: string[]; includeOpponentTop: boolean; opponentTeamId?: string }) => {
      if (!selectedHandCardId) return [];
      if (state.phase !== 'playing') return [];
      if (state.currentPlayerId !== viewerPlayerId) return [];
      return getContextualMoves(state, viewerPlayerId, selectedHandCardId, targets);
    },
    [state, viewerPlayerId, selectedHandCardId]
  );

  const showMovesOrPlay = useCallback(
    (x: number, y: number, moves: MoveOption[], emptyDiagnostics?: string | Action[]) => {
      const unique = dedupeMoves(moves);
      if (unique.length <= 0) {
        setMovePopup(null);
        if (emptyDiagnostics) {
          if (typeof emptyDiagnostics === 'string') {
            showErrorToast(emptyDiagnostics, x, y);
          } else {
            let reason: string | null = null;
            for (const candidate of emptyDiagnostics) {
              try {
                applyAction(state, candidate);
              } catch (err) {
                const raw = err instanceof Error ? err.message : 'Not allowed.';
                reason = shortMoveError(raw);
                break;
              }
            }
            showErrorToast(reason || 'NOT ALLOWED', x, y);
          }
        } else {
          showErrorToast('NOT ALLOWED', x, y);
        }
        return;
      }
      const takeTypes = new Set<Action['type']>([
        ACTIONS.TAKE_SINGLE,
        ACTIONS.TAKE_SUBSET,
        ACTIONS.TAKE_PILE,
      ]);
      if (unique.length === 1) {
        const only = unique[0] as Action;
        if (only.type === ACTIONS.DROP || takeTypes.has(only.type)) {
          safeDispatch(only);
          return;
        }
      }
      setMovePopup({ x, y, moves: unique as Action[] });
    },
    [safeDispatch, showErrorToast, state]
  );

  const onFeltDropClick = useCallback(
    (event: React.MouseEvent) => {
      if (!selectedHandCardId) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.table-panel')) return;
      if (target.closest('button, a, input, select, textarea, [role="button"]')) return;
      if (target.closest('.engine-card')) return;
      if (target.closest('.engine-move-popup, .engine-absorb-popup, .engine-drawer, .engine-modal-card')) return;

      setSelectedTableLooseIds(new Set());
      setSelectedBuildIds(new Set());
      setSelectedStayIds(new Set());
      setSelectedOpponentTopTeamId(null);
      setSelectedPendingRoyalOwnerId(null);
      setMovePopup(null);

      const moves = computeContextMoves({
        looseCardIds: [],
        buildPileIds: [],
        stayPileIds: [],
        includeOpponentTop: false,
      });
      showMovesOrPlay(
        event.clientX,
        event.clientY,
        moves.filter((m) => m.type === ACTIONS.DROP),
        [{ type: ACTIONS.DROP, playerId: viewerPlayerId, cardId: selectedHandCardId }]
      );
    },
    [selectedHandCardId, computeContextMoves, showMovesOrPlay, viewerPlayerId]
  );

  const clearSelection = () => {
    setSelectedTableLooseIds(new Set());
    setSelectedBuildIds(new Set());
    setSelectedStayIds(new Set());
    setSelectedOpponentTopTeamId(null);
    setMovePopup(null);
  };

  const selectedHandCard = useMemo(() => {
    if (!selectedHandCardId) return null;
    return state.hands[viewerPlayerId]?.find((card) => card.id === selectedHandCardId) ?? null;
  }, [state.hands, viewerPlayerId, selectedHandCardId]);

  const pendingAbsorption = state.turnContext.pendingAbsorption?.playerId === viewerPlayerId ? state.turnContext.pendingAbsorption : null;
  const isSetup = state.phase === 'betweenRounds';
  const isGameOver = state.phase === 'gameOver';
  const showRound2Modal =
    !stateOverride && !disableLocalControls && state.phase === 'betweenRounds' && state.mode === '2p' && state.meta.roundIndex === 1 && state.deck.length > 0;
  const pendingOwnersByTeam = useMemo(() => {
    const byTeam: Record<string, string[]> = {};
    state.players.forEach((player) => {
      if ((state.royalPending[player.id] || []).length <= 0) return;
      if (!byTeam[player.teamId]) byTeam[player.teamId] = [];
      byTeam[player.teamId].push(player.id);
    });
    return byTeam;
  }, [state.players, state.royalPending]);

  const playerById = useMemo(() => new Map(state.players.map((p) => [p.id, p])), [state.players]);
  const viewerTeamId = playerById.get(viewerPlayerId)?.teamId || 'T1';
  const bottomSeatIds = useMemo(() => {
    const partner = state.players.find((p) => p.teamId === viewerTeamId && p.id !== viewerPlayerId)?.id || null;
    return partner ? [viewerPlayerId, partner] : [viewerPlayerId];
  }, [state.players, viewerTeamId, viewerPlayerId]);
  const topSeatIds = useMemo(
    () => state.players.map((p) => p.id).filter((id) => !bottomSeatIds.includes(id)),
    [state.players, bottomSeatIds],
  );

  const leftTeamId = teamOrder[0] || null;
  const rightTeamId = teamOrder[1] || null;

	  const renderTeamCollectionColumn = (teamId: string): JSX.Element => {
    const stack = state.collections[teamId];
    const cards = stack?.cards || [];
    const top = cards[cards.length - 1];
    const covered = top && ['J', 'Q', 'K'].includes(top.rank);
    const topCardName = top ? compactCard(top) : 'empty';
	    const teamPendingOwners = pendingOwnersByTeam[teamId] || [];
      const collectionHint = fmtHint([
        `Team: ${teamLabelById[teamId] ?? teamId}`,
        `Cards: ${cards.length}`,
        covered ? 'Status: Covered by royal' : 'Status: Uncovered',
      ]);

	    return (
		      <div className="table-team-panel-inner">
            <div className="pile-hint-wrapper">
		        <button
		          className={`pile-visual-btn ${selectedOpponentTopTeamId === teamId ? 'pile-selected' : ''}`.trim()}
              title={topCardName}
              onPointerDown={onPilePointerDown(`collection-${teamId}`, collectionHint)}
              onPointerUp={onPilePointerUp}
              onPointerCancel={onPilePointerUp}
              onMouseEnter={openPileHintFromEvent(`collection-${teamId}`, collectionHint)}
              onMouseLeave={closePileHint}
              onFocus={openPileHintFromEvent(`collection-${teamId}`, collectionHint)}
              onBlur={closePileHint}
		          onClick={(event) => {
                if (shouldSuppressPileClick(event)) return;
		            event.stopPropagation();
                if (teamId === viewerTeamId && selectedPendingRoyalOwnerId === viewerPlayerId && (state.royalPending[viewerPlayerId] || []).length > 0) {
                  safeDispatch({ type: ACTIONS.PLACE_PENDING_ROYALS, playerId: viewerPlayerId });
                  return;
                }
		            const nextTeam = selectedOpponentTopTeamId === teamId ? null : teamId;
		            setSelectedOpponentTopTeamId(nextTeam);
		            setSelectedPendingRoyalOwnerId(null);
		            setMovePopup(null);
		            if (!selectedHandCardId || !nextTeam) return;
		            const selectedLoose = Array.from(selectedTableLooseIds);
		            const moves = computeContextMoves({
		              looseCardIds: selectedLoose,
		              buildPileIds: Array.from(selectedBuildIds),
		              stayPileIds: Array.from(selectedStayIds),
		              includeOpponentTop: true,
		              opponentTeamId: nextTeam,
		            });
		            showMovesOrPlay(event.clientX, event.clientY, moves.filter((m) => m.type !== ACTIONS.DROP));
		          }}
          type="button"
        >
          <div className="pile-visual-row">
            {renderPileStack(cards, `table-collection-${teamId}`)}
          </div>
        </button>
            </div>

        {teamPendingOwners.length ? (
          <div className="pending-row mt-2" aria-label="Pending royals row">
            {teamPendingOwners.map((ownerId) => {
              const ownerPending = state.royalPending[ownerId] || [];
              const isViewerPending = ownerId === viewerPlayerId;
              const pendingHint = fmtHint([
                'Pending Royals',
                `Owner: ${canonicalPlayerLabel(ownerId)}`,
                `Cards: ${ownerPending.length}`,
                ...(ownerPending.length > 0 ? [
                  `Royals: ${ownerPending.map(compactCard).join(' + ')}`
                ] : []),
                isViewerPending ? 'Click: place pending royals' : 'Status: Waiting for placement',
              ]);
              const pendingPileId = `pending-${ownerId}`;
              return (
                <div key={`pending-${ownerId}`} className="pile-hint-wrapper">
                <button
                  className={`pile-visual-btn ${selectedPendingRoyalOwnerId === ownerId ? 'pile-selected' : ''}`.trim()}
                  title={ownerPending.length > 0 ? `${ownerPending.length} pending royals` : 'No pending royals'}
                  onPointerDown={onPilePointerDown(pendingPileId, pendingHint)}
                  onPointerUp={onPilePointerUp}
                  onPointerCancel={onPilePointerUp}
                  onMouseEnter={openPileHintFromEvent(pendingPileId, pendingHint)}
                  onMouseLeave={closePileHint}
                  onFocus={openPileHintFromEvent(pendingPileId, pendingHint)}
                  onBlur={closePileHint}
	                  onClick={(event) => {
                      if (shouldSuppressPileClick(event)) return;
	                    setSelectedPendingRoyalOwnerId((prev) => (prev === ownerId ? null : ownerId));
	                    if (!isViewerPending) return;
	                    setSelectedHandCardId(null);
	                    setSelectedTableLooseIds(new Set());
	                    setSelectedBuildIds(new Set());
	                    setSelectedStayIds(new Set());
	                    setSelectedOpponentTopTeamId(null);
	                    setMovePopup(null);
                  }}
                  type="button"
                >
                  <div className="pile-visual-row">
                    {renderPileStack(ownerPending, `table-pending-${ownerId}`)}
                  </div>
                </button>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderSeatCard = (playerId: string | null): JSX.Element | null => {
    if (!playerId) return null;
    const player = playerById.get(playerId);
    if (!player) return null;
    const isViewer = playerId === viewerPlayerId;
    const isAiSeat = aiActive && aiPlayers.has(playerId);
    const isActiveTurnSeat = state.phase === 'playing' && state.currentPlayerId === playerId;
    const handCount = state.hands[playerId]?.length || 0;
    const handCards = state.hands[playerId] || [];
    const teamStack = state.collections[player.teamId];
    const teamCards = teamStack?.cards || [];
    const hintHeader = isAiSeat ? 'Computer' : 'Player';
    
    // Get player's builds
    const playerBuilds = state.builds.filter(build => build.ownerPlayerId === playerId);
    const playerStays = state.stays.filter(stay => stay.ownerPlayerId === playerId);
    
    // Build detailed hint lines
    const collectionTopCard = teamCards[teamCards.length - 1];
      const seatHintLines = [
      hintHeader,
      canonicalPlayerLabel(playerId),
      `${handCount} in hand`,
      `${teamCards.length} collected${collectionTopCard ? ` (top: ${compactCard(collectionTopCard)})` : ''}`,
      ...(playerBuilds.length > 0 ? [
        `Builds: ${playerBuilds.length}`,
        ...playerBuilds.map(build => 
          `  ${build.isSolid ? 'Closed' : 'Open'} build ${build.value} (${build.cards.length} cards)`
        )
      ] : []),
      ...(playerStays.length > 0 ? [
        `Stays: ${playerStays.length}`,
        ...playerStays.map(stay => 
          `  Stay ${stay.value} (${stay.cards.length} cards)`
        )
      ] : []),
      ...(playerBuilds.length === 0 && playerStays.length === 0 ? ['No builds or stays'] : [])
    ];

    return (
      <div className={`poker-seat poker-seat-opponent ${isViewer ? 'viewer' : ''}`.trim()} aria-label={`${playerId} seat`}>
        <div className="poker-avatar-wrap" tabIndex={0} aria-label={`${playerId} info`}>
          <div className={`poker-avatar ${isViewer ? 'viewer' : ''}`} aria-hidden="true" />
          {isActiveTurnSeat ? (
            <div className="poker-thinking-bubble" role="status" aria-label="Player turn indicator">
              {isAiSeat ? 'Thinking…' : isViewer ? 'Your turn' : 'Turn'}
            </div>
          ) : null}
          <div className="poker-seat-hint" role="tooltip">
            {seatHintLines.map((line, idx) => (
              <div key={`${playerId}-hint-${idx}`}>{line}</div>
            ))}
          </div>
        </div>
        <div
          className="poker-seat-cards-row"
          aria-label={`${playerId} hand`}
          onClick={(event) => {
            if (!isViewer) return;
            if (!selectedHandCardId) return;
            if ((event.target as HTMLElement | null)?.closest('button.engine-card')) return;
            cancelPickedUpHandCard();
          }}
        >
          {isAiVsAi || isViewer
            ? handCards.map((card, idx) => {
                if (isViewer && selectedHandCardId === card.id) return null;
                return renderCard(
                  card,
                  `poker-seat-card ${isViewer && selectedHandCardId === card.id ? 'selected' : ''}`.trim(),
                  isViewer
                    ? (event) => {
                        event.stopPropagation();
                        setMovePopup(null);
                        setSelectedPendingRoyalOwnerId(null);
                        setSelectedTableLooseIds(new Set());
                        setSelectedBuildIds(new Set());
                        setSelectedStayIds(new Set());
                        setSelectedOpponentTopTeamId(null);
                        const willSelect = selectedHandCardId !== card.id;
                        if (!willSelect) {
                          cancelPickedUpHandCard();
                          return;
                        }
                        setSelectedHandCardId(card.id);
                        setSelectedHandGhostPos({ x: event.clientX, y: event.clientY });
                      }
                    : undefined,
                  { zIndex: isViewer && selectedHandCardId === card.id ? 999 : idx }
                );
              })
            : Array.from({ length: Math.min(handCount, 8) }).map((_, idx) => (
                <span key={`${playerId}-seat-${idx}`} className="engine-card back poker-seat-card">?</span>
              ))}
        </div>
        <div className="poker-seat-collection" title={`${player.teamId} collection`}>
          {renderPileStack(teamCards, `seat-collection-${playerId}`)}
        </div>
      </div>
    );
  };

  useImperativeHandle(
    ref,
    () => ({
      newGame: () => {
        if (!stateOverride && !disableLocalControls) {
          safeDispatch(newGame({ mode: lockedMode || mode, seed: `${Date.now()}` }));
        }
      },
      startRound: () => {
        if (!stateOverride && !disableLocalControls && isSetup) {
          safeDispatch(startRound(state.meta.roundIndex));
        }
      },
      clearSelection: () => clearSelection(),
      setMode: (nextMode: GameState['mode']) => {
        if (!stateOverride && !disableLocalControls) {
          setMode(nextMode);
        }
      },
    }),
    [stateOverride, disableLocalControls, lockedMode, mode, isSetup, state.meta.roundIndex, safeDispatch]
  );

  useEffect(() => {
    if (!onHudChange) return;
    onHudChange({
      round: state.meta.roundIndex + 1,
      currentPlayerId: state.currentPlayerId,
      viewerPlayerId,
      deckCount: effectiveDeckRemaining,
      phase: state.phase,
      mode: lockedMode || mode,
      isSetup,
    });
  }, [onHudChange, state, viewerPlayerId, lockedMode, mode, isSetup, effectiveDeckRemaining]);

  return (
    <div className="engine-root">
      <input
        ref={aiProfileFileRef}
        type="file"
        accept="application/json"
        className="visually-hidden"
        onChange={() => {
          onAiProfileFileChange().catch(() => {});
        }}
      />
      <SettingsModal
        open={settingsOpen}
        userLabel={canonicalPlayerLabel(viewerPlayerId)}
        onClose={() => setSettingsOpen(false)}
        onApply={(next) => setGameSettings(next)}
      />
      <ConfirmModal
        open={restartOpen}
        title="Restart Game"
        message="Are you sure you want to restart? Current progress will be lost."
        confirmLabel="Restart"
        confirmVariant="danger"
        onCancel={() => setRestartOpen(false)}
        onConfirm={() => {
          setRestartOpen(false);
          restartLocalGame();
        }}
      />
      {showRound2Modal ? (
        <div className="round-overlay" role="dialog" aria-modal="true" aria-label="Start next round">
          <div className="round-modal">
            <div className="round-actions">
              <button className="btn btn-warning" type="button" onClick={() => safeDispatch(startRound(state.meta.roundIndex))}>
                Start Round 2
              </button>
            </div>
          </div>
        </div>
      ) : null}

		      {movePopup ? (
	        <div
	          ref={movePopupRef}
	          className="engine-move-popup"
	          style={{ left: movePopup.x, top: movePopup.y }}
	          role="menu"
	          aria-label="Available actions"
	          onClick={(e) => e.stopPropagation()}
	          onMouseDown={(e) => e.stopPropagation()}
	        >
	          {movePopup.moves.map((move, idx) => {
	            const label = (move as any).label || move.type;
	            return (
	              <button
	                key={`${move.type}-${idx}`}
	                className="engine-move-popup-btn"
	                type="button"
	                title={label}
	                onClick={(event) => {
	                  event.stopPropagation();
	                  safeDispatch(move);
	                }}
	              >
	                {label}
	              </button>
	            );
	          })}
	        </div>
		      ) : null}

        {errorToast ? (
          <div
            className="engine-toast"
            style={{ left: `${errorToast.x}px`, top: `${errorToast.y}px` }}
            role="status"
            aria-label="Move error"
          >
            {errorToast.message}
          </div>
        ) : null}

        {pileHint ? (
          <div
            ref={pileHintRef}
            className={`poker-seat-hint pile-hint pile-hint-floating ${pileHintLayout?.placement === 'top' ? 'top' : 'bottom'}`.trim()}
            style={{
              display: 'block',
              position: 'fixed',
              left: `${(pileHintLayout?.pos.left ?? (pileHint.anchor.left + pileHint.anchor.width / 2))}px`,
              top: `${(pileHintLayout?.pos.top ?? (pileHint.anchor.bottom + 8))}px`,
              transform: 'translateX(-50%)',
              visibility: pileHintLayout ? 'visible' : 'hidden',
            }}
            role="tooltip"
          >
            {pileHint.lines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        ) : null}

	      {pendingAbsorption ? (
	        <div className="engine-absorb-popup" role="dialog" aria-label="Absorption choices">
	          <div className="engine-absorb-title">Absorption</div>
	          <div className="small mb-2">Target value: {pendingAbsorption.value}</div>
	          <div className="d-grid gap-1">
	            {pendingAbsorption.choices.map((choice) => (
	              <button
	                key={choice.id}
	                className="btn btn-outline-warning btn-sm text-start"
	                type="button"
	                onClick={() => safeDispatch(absorbChoose(viewerPlayerId, choice.id))}
	              >
	                {choice.type === 'tableSubset' ? `Table: ${choice.tableCardIds.join(', ')}` : `Top: ${choice.collectionTopTeamId}`}
	              </button>
	            ))}
	            <button className="btn btn-secondary btn-sm" type="button" onClick={() => safeDispatch(absorbSkip(viewerPlayerId))}>
	              End Absorption
	            </button>
	          </div>
	        </div>
	      ) : null}

	      {logOpen ? (
	        <>
	          <button className="engine-modal-backdrop" type="button" aria-label="Close turn log" onClick={() => setLogOpen(false)} />
	          <div className="engine-modal-card engine-log-modal" role="dialog" aria-modal="true" aria-label="Turn log">
            <div className="engine-modal-header">
              <div className="engine-modal-title">
                <ScrollText size={16} aria-hidden="true" />
                <span>Turn Log</span>
              </div>
              <button className="engine-iconbtn" type="button" onClick={() => setLogOpen(false)} aria-label="Close">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="engine-modal-body engine-log">
              <ul className="mb-0 ps-3">
                {state.turnContext.log
                  .slice()
                  .reverse()
                  .map((line, idx) => (
                    <li key={`${idx}-${line}`}>{line}</li>
                  ))}
              </ul>
            </div>
	          </div>
	        </>
	      ) : null}

        {scoreSummaryOpen ? (
          <>
            <div className="engine-modal-backdrop" aria-hidden="true" />
            <div className="engine-modal-card engine-score-modal" role="dialog" aria-modal="true" aria-label="Score summary">
              <div className="engine-score-header">
                <div className="engine-score-title">SCORE SUMMARY</div>
              </div>
              <div className="engine-modal-body">
                <div className="engine-score-grid">
                  {Object.entries(state.meta.scores || {})
                    .slice()
                    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
                    .map(([teamId, score]) => (
                      <section key={teamId} className="engine-score-panel" aria-label={`Team ${teamId} score`}>
                        <div className="engine-score-panel-title">
                          <span>Team {teamId}</span>
                          <span className="engine-score-total">
                            <Flag size={14} aria-hidden="true" /> {score.total} pts
                          </span>
                        </div>
                        {score.items?.length ? (
                          <ul className="engine-score-items">
                            {score.items.map((item, idx) => (
                              <li key={`${teamId}-${item.ruleId}-${idx}`} className="engine-score-item">
                                <span className="engine-score-item-label">{item.label}</span>
                                <span className="engine-score-item-points">+{item.points}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="engine-score-empty">No scoring items.</div>
                        )}
                      </section>
                    ))}
                </div>

                <div className="engine-score-actions" role="group" aria-label="Game over actions">
                  <button
                    className="engine-score-btn primary"
                    type="button"
                    onClick={playMode === 'ONLINE' ? restartFromScoreSummaryOnline : restartFromScoreSummary}
                  >
                    Restart
                  </button>
                  <button className="engine-score-btn danger" type="button" onClick={quitFromScoreSummary}>
                    Quit
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}

	      {menuOpen ? (
	        <>
	          <button className="engine-drawer-backdrop" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
	          <aside className="engine-drawer" role="dialog" aria-modal="true" aria-label="Menu">
	            <div className="engine-drawer-header">
	              <div className="engine-logo" aria-label="Omulongo">
	                <Dices size={16} aria-hidden="true" />
	                <span>omu</span>
	                <span className="diamond">1♦</span>
	                <span>ngo</span>
	              </div>
	              <button className="engine-iconbtn" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu">
	                <X size={16} aria-hidden="true" />
	              </button>
	            </div>

	            <div className="engine-drawer-body">
	              <div className="engine-drawer-section">
	                <div className="engine-drawer-label">Status</div>
	                <div className="engine-drawer-chips">
	                  {playMode === 'LOCAL' && aiTakeover ? (
	                    <div className="engine-chip chip-ai" title="AI takeover (toggle: Ctrl+. on Windows, Cmd+. on Mac)">
	                      <span>AI takeover</span>
	                    </div>
	                  ) : null}
	                  {playMode === 'ONLINE' && inviteCode ? (
	                    <div className="engine-chip chip-code">
	                      <PlugZap size={14} aria-hidden="true" />
	                      <span>Code {inviteCode}</span>
	                    </div>
	                  ) : null}
	                  {playMode === 'ONLINE' && onlineSeatLabel ? (
	                    <div className="engine-chip chip-seat">
	                      <CircleUserRound size={14} aria-hidden="true" />
	                      <span>Seat {onlineSeatLabel}</span>
	                    </div>
	                  ) : null}
	                  {playMode === 'ONLINE' && onlineTeamLabel ? (
	                    <div className="engine-chip chip-team">
	                      <span>Team {onlineTeamLabel}</span>
	                    </div>
	                  ) : null}
	                  {playMode === 'ONLINE' && onlineStatusLabel ? (
	                    <div className="engine-chip muted chip-status">
	                      <span>Status {onlineStatusLabel}</span>
	                    </div>
	                  ) : null}
	                  <div className="engine-chip chip-round">
	                    <Flag size={14} aria-hidden="true" />
	                    <span>Round {state.meta.roundIndex + 1}</span>
	                  </div>
	                  <div className="engine-chip chip-turn">
	                    <Crosshair size={14} aria-hidden="true" />
	                    <span>Turn {canonicalPlayerLabel(state.currentPlayerId)}</span>
	                  </div>
	                  <div className="engine-chip chip-deck">
	                    <Layers size={14} aria-hidden="true" />
	                    <span>{effectiveDeckRemaining} left</span>
	                  </div>
	                  <div className="engine-chip muted chip-phase">
	                    <Hourglass size={14} aria-hidden="true" />
	                    <span>{state.phase}</span>
	                  </div>
	                  <div className="engine-chip muted chip-you">
	                    <CircleUserRound size={14} aria-hidden="true" />
	                    <span>You {canonicalPlayerLabel(viewerPlayerId)}</span>
	                  </div>
	                </div>
	              </div>

	                  <div className="engine-drawer-section">
	                <div className="engine-drawer-label">Menu</div>
	                <div className="engine-drawer-actions">
	                  <button
	                    className="engine-iconbtn"
	                    onClick={() => {
	                      setMenuOpen(false);
	                      setLogOpen(true);
	                    }}
	                    type="button"
	                    title="Turn Log"
	                  >
	                    <ScrollText size={16} aria-hidden="true" />
	                    Turn Log
	                  </button>
	                  {showReconnect ? (
	                    <button
	                      className="engine-iconbtn"
	                      onClick={() => {
	                        setMenuOpen(false);
	                        onReconnect?.();
	                      }}
	                      type="button"
	                      title="Reconnect"
	                    >
	                      <PlugZap size={16} aria-hidden="true" />
	                      Reconnect
	                    </button>
	                  ) : null}
	                  {playMode === 'ONLINE' && onLeaveOnline ? (
	                    <button
	                      className="engine-iconbtn"
	                      onClick={() => {
	                        setMenuOpen(false);
	                        onLeaveOnline?.();
	                      }}
	                      type="button"
	                      title="Leave Online Game"
	                    >
	                      Leave
	                    </button>
	                  ) : null}
	                  {onQuitRequest ? (
	                    <button
	                      className="engine-iconbtn"
	                      onClick={() => {
	                        setMenuOpen(false);
	                        onQuitRequest?.();
	                      }}
	                      type="button"
	                      title="Quit"
	                    >
	                      Quit
	                    </button>
	                  ) : null}
	                  {onNewGameRequest ? (
	                    <button
	                      className="engine-iconbtn"
	                      onClick={() => {
	                        setMenuOpen(false);
	                        onNewGameRequest?.();
	                      }}
	                      type="button"
	                      title="New Game"
	                    >
	                      New Game
	                    </button>
	                  ) : null}
	                  {!stateOverride && !disableLocalControls ? (
	                    <button
	                      className="engine-iconbtn"
	                      onClick={() => {
	                        setMenuOpen(false);
	                        setRestartOpen(true);
	                      }}
	                      type="button"
	                      title="Restart"
	                    >
	                      Restart
	                    </button>
	                  ) : null}
	                  <button
	                    className="engine-iconbtn"
	                    onClick={() => {
	                      setMenuOpen(false);
	                      setSettingsOpen(true);
	                    }}
	                    type="button"
	                    title="Settings"
	                  >
	                    <Settings size={16} aria-hidden="true" />
	                    Settings
	                  </button>
	                </div>
	              </div>
	            </div>
	          </aside>
	        </>
	      ) : null}

		      <header className="engine-topbar" ref={topbarRef}>
		        <div className="engine-topbar-inner">
		          <div aria-hidden="true" />
		          <div className="engine-logo" aria-label="Omulongo">
		            <Dices size={16} aria-hidden="true" />
		            <span>omu</span>
		            <span className="diamond">1♦</span>
		            <span>ngo</span>
		          </div>
		          <div className="engine-topbar-actions">
		            <button className="engine-iconbtn" type="button" title="Turn Log" onClick={() => setLogOpen(true)}>
		              <ScrollText size={16} aria-hidden="true" />
		            </button>
		            <button className="engine-iconbtn" type="button" title="Menu" onClick={() => setMenuOpen(true)}>
		              <Menu size={16} aria-hidden="true" />
		            </button>
		          </div>
	        </div>
	      </header>
      <div className={`engine-stage ${selectedHandCardId ? 'dragging' : ''}`.trim()} ref={stageRef}>
        <div className="engine-stage-content">
          <div className="engine-stage-inner" ref={stageContentRef}>
            <div className="container-fluid p-0">
              <div className="px-0 pb-3">
          <div className="mb-2" />

          <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
            {/* selection clearing lives on the player seat card */}
          </div>

          {error ? <div className="alert alert-danger py-2">{error}</div> : null}

          <div className="poker-layout">
            <div className="poker-columns">
              <div className="poker-left">
                {selectedHandCard && selectedHandGhostPos ? (
                  <div className="hand-drag-ghost" style={{ left: selectedHandGhostPos.x + 12, top: selectedHandGhostPos.y + 12 }}>
                    <span className={`engine-card ${isRed(selectedHandCard) ? 'red' : ''} selected drag-ghost`.trim()}>
                      <span>{selectedHandCard.rank}</span>
                      <span>{suitSymbol(selectedHandCard.suit)}</span>
                    </span>
                  </div>
                ) : null}

                {topSeatIds.length ? (
                  <div className="poker-seat-row poker-seat-row-top" role="region" aria-label="Opponents">
                    {topSeatIds.map((id) => (
                      <React.Fragment key={`top-seat-${id}`}>{renderSeatCard(id)}</React.Fragment>
                    ))}
                  </div>
                ) : null}

	                <div className="poker-table-wrap">
		                  <div className="poker-table" role="region" aria-label="Game table" onClick={onFeltDropClick}>
		                    <div className="poker-center">
		                      <div className="table-center-row">
		                        {leftTeamId ? (
		                          <div className="table-team-panel table-team-left">
		                            <div className="visually-hidden">{teamLabelById[leftTeamId] ?? leftTeamId}</div>
		                            {renderTeamCollectionColumn(leftTeamId)}
		                          </div>
		                        ) : null}

			                        <div
			                          className="table-panel poker-center-panel"
                                title="Table loose cards"
			                          onClick={(event) => {
			                            if (!selectedHandCardId) return;
			                            setSelectedTableLooseIds(new Set());
			                            setSelectedBuildIds(new Set());
			                            setSelectedStayIds(new Set());
			                            setSelectedOpponentTopTeamId(null);
			                            setSelectedPendingRoyalOwnerId(null);
			                            setMovePopup(null);
			                            const moves = computeContextMoves({
			                              looseCardIds: [],
			                              buildPileIds: [],
			                              stayPileIds: [],
			                              includeOpponentTop: false,
			                            });
			                            showMovesOrPlay(
			                              event.clientX,
			                              event.clientY,
			                              moves.filter((m) => m.type === ACTIONS.DROP)
			                            );
			                          }}
			                        >
			                          <div className="table-loose-wrap">
			                            <AnimatePresence mode="popLayout">
			                              {state.tableLoose.length
			                                ? state.tableLoose.map((card) =>
			                                    renderCard(
			                                      card,
			                                      selectedTableLooseIds.has(card.id) ? 'highlight-target' : '',
			                                      (event) => {
			                                        event.stopPropagation();
			                                        const next = new Set(selectedTableLooseIds);
			                                        if (next.has(card.id)) next.delete(card.id);
			                                        else next.add(card.id);
			                                        setSelectedTableLooseIds(next);
			                                        setSelectedPendingRoyalOwnerId(null);
			                                        setMovePopup(null);
			                                        if (!selectedHandCardId) return;
			                                        const moves = computeContextMoves({
			                                          looseCardIds: Array.from(next),
			                                          buildPileIds: Array.from(selectedBuildIds),
			                                          stayPileIds: Array.from(selectedStayIds),
			                                          includeOpponentTop: Boolean(selectedOpponentTopTeamId),
			                                          opponentTeamId: selectedOpponentTopTeamId || undefined,
			                                        });
			                                        showMovesOrPlay(
			                                          event.clientX,
			                                          event.clientY,
			                                          moves.filter((m) => m.type !== ACTIONS.DROP)
			                                        );
			                                      }
			                                    )
			                                  )
			                                : null}
			                            </AnimatePresence>
			                          </div>

		                          <div className="build-stay-grid poker-piles">
	                          <div className="engine-pile">
	                            <div className="visually-hidden">Builds</div>
                                <div className="pile-grid" aria-label="Build piles grid">
		                            {state.builds.length
                              ? state.builds.map((build) => {
                                  const topCard = build.cards[build.cards.length - 1];
                                  const topCardName = topCard ? compactCard(topCard) : `Build ${build.value}`;
                                  const buildHint = fmtHint([
                                    'Build pile',
                                    `Value: ${build.value}`,
                                    `Owner: ${canonicalPlayerLabel(build.ownerPlayerId)}`,
                                    `Cards: ${build.cards.length}`,
                                  ]);
                                  return (
                                    <div
                                      key={build.id}
                                      className="pile-hint-wrapper"
                                    >
		                                  <button
		                                    className={`pile-visual-btn ${selectedBuildIds.has(build.id) ? 'pile-selected' : ''}`.trim()}
                                        title={topCardName}
                                        onPointerDown={onPilePointerDown(`build-${build.id}`, buildHint)}
                                        onPointerUp={onPilePointerUp}
                                        onPointerCancel={onPilePointerUp}
                                        onMouseEnter={openPileHintFromEvent(`build-${build.id}`, buildHint)}
                                        onMouseLeave={closePileHint}
                                        onFocus={openPileHintFromEvent(`build-${build.id}`, buildHint)}
                                        onBlur={closePileHint}
		                                    onClick={(event) => {
                                          if (shouldSuppressPileClick(event)) return;
		                                      event.stopPropagation();
		                                      const next = new Set(selectedBuildIds);
		                                      if (next.has(build.id)) next.delete(build.id);
		                                      else next.add(build.id);
		                                      setSelectedBuildIds(next);
		                                      setSelectedPendingRoyalOwnerId(null);
		                                      setMovePopup(null);
		                                      if (!selectedHandCardId) return;
		                                      const selectedLoose = Array.from(selectedTableLooseIds);
		                                      const moves = computeContextMoves({
		                                        looseCardIds: selectedLoose,
		                                        buildPileIds: Array.from(next),
		                                        stayPileIds: [],
		                                        includeOpponentTop: Boolean(selectedOpponentTopTeamId),
		                                        opponentTeamId: selectedOpponentTopTeamId || undefined,
		                                      });
                                      const owner = state.players.find((p) => p.id === build.ownerPlayerId) || null;
                                      const diag: Action[] = [];
                                      if (build.ownerPlayerId === viewerPlayerId) {
                                        diag.push({ type: ACTIONS.REINFORCE_BUILD, playerId: viewerPlayerId, pileId: build.id, cardIdFromHand: selectedHandCardId });
                                      } else if (owner && owner.teamId === viewerTeamId) {
                                        diag.push({
                                          type: ACTIONS.ADD_TO_BUILD,
                                          playerId: viewerPlayerId,
                                          pileId: build.id,
                                          playedCardIdFromHand: selectedHandCardId,
                                          tableCardIdsUsed: selectedLoose,
                                          includeOpponentTop: Boolean(selectedOpponentTopTeamId),
                                          opponentTeamId: selectedOpponentTopTeamId || undefined,
                                        });
                                      } else {
                                        diag.push({ type: ACTIONS.BUILD_UPDATE, playerId: viewerPlayerId, pileId: build.id, playedCardIdFromHand: selectedHandCardId });
                                      }
		                                      showMovesOrPlay(
		                                        event.clientX,
		                                        event.clientY,
		                                        moves.filter((m) => m.type !== ACTIONS.DROP),
                                        diag
		                                      );
		                                    }}
		                                  >
		                                    <div className="pile-visual-row">
                                      {renderPileStack(build.cards, `build-${build.id}`)}
                                    </div>
                                  </button>
                                  </div>
                                  );
                                })
	                              : null}
                                </div>
	                          </div>

	                          <div className="engine-pile">
	                            <div className="visually-hidden">Stays</div>
                                <div className="pile-grid" aria-label="Stay piles grid">
	                            {state.stays.length
                              ? state.stays.map((stay) => {
                                  const topCard = stay.cards[stay.cards.length - 1];
                                  const topCardName = topCard ? compactCard(topCard) : `Stay ${stay.value}`;
                                  const stayHint = fmtHint([
                                    'Stay pile',
                                    `Value: ${stay.value}`,
                                      `Owner: ${canonicalPlayerLabel(stay.ownerPlayerId)}`,
                                    `Cards: ${stay.cards.length}`,
                                  ]);
                                  return (
                                    <div
                                      key={stay.id}
                                      className="pile-hint-wrapper"
                                    >
	                                  <button
	                                    className={`pile-visual-btn ${selectedStayIds.has(stay.id) ? 'pile-selected' : ''}`.trim()}
                                      title={topCardName}
                                      onPointerDown={onPilePointerDown(`stay-${stay.id}`, stayHint)}
                                      onPointerUp={onPilePointerUp}
                                      onPointerCancel={onPilePointerUp}
                                      onMouseEnter={openPileHintFromEvent(`stay-${stay.id}`, stayHint)}
                                      onMouseLeave={closePileHint}
                                      onFocus={openPileHintFromEvent(`stay-${stay.id}`, stayHint)}
                                      onBlur={closePileHint}
	                                    onClick={(event) => {
                                        if (shouldSuppressPileClick(event)) return;
	                                      event.stopPropagation();
	                                      const next = new Set(selectedStayIds);
	                                      if (next.has(stay.id)) next.delete(stay.id);
	                                      else next.add(stay.id);
	                                      setSelectedStayIds(next);
	                                      setSelectedPendingRoyalOwnerId(null);
	                                      setMovePopup(null);
	                                      if (!selectedHandCardId) return;
	                                      const selectedLoose = Array.from(selectedTableLooseIds);
	                                      const moves = computeContextMoves({
	                                        looseCardIds: selectedLoose,
	                                        buildPileIds: [],
	                                        stayPileIds: Array.from(next),
	                                        includeOpponentTop: Boolean(selectedOpponentTopTeamId),
	                                        opponentTeamId: selectedOpponentTopTeamId || undefined,
	                                      });
                                      const owner = state.players.find((p) => p.id === stay.ownerPlayerId) || null;
                                      const diag: Action[] = [];
                                      if (stay.ownerPlayerId === viewerPlayerId) {
                                        diag.push({ type: ACTIONS.REINFORCE_STAY, playerId: viewerPlayerId, pileId: stay.id, cardIdFromHand: selectedHandCardId });
                                      } else if (owner && owner.teamId === viewerTeamId) {
                                        diag.push({
                                          type: ACTIONS.ADD_TO_STAY,
                                          playerId: viewerPlayerId,
                                          pileId: stay.id,
                                          playedCardIdFromHand: selectedHandCardId,
                                          tableCardIdsUsed: selectedLoose,
                                          includeOpponentTop: Boolean(selectedOpponentTopTeamId),
                                          opponentTeamId: selectedOpponentTopTeamId || undefined,
                                        });
                                      } else {
                                        diag.push({ type: ACTIONS.TAKE_PILE, playerId: viewerPlayerId, cardIdFromHand: selectedHandCardId, target: { type: 'stay', pileId: stay.id } });
                                      }
	                                      showMovesOrPlay(
	                                        event.clientX,
	                                        event.clientY,
	                                        moves.filter((m) => m.type !== ACTIONS.DROP),
                                        diag
	                                      );
	                                    }}
	                                  >
	                                    <div className="pile-visual-row">
                                      {renderPileStack(stay.cards, `stay-${stay.id}`)}
                                    </div>
                                  </button>
                                  </div>
                                  );
                                })
	                              : null}
                                </div>
	                          </div>
		                          </div>
		                        </div>

		                        {rightTeamId ? (
		                          <div className="table-team-panel table-team-right">
		                            <div className="visually-hidden">{teamLabelById[rightTeamId] ?? rightTeamId}</div>
		                            {renderTeamCollectionColumn(rightTeamId)}
		                          </div>
		                        ) : null}
		                      </div>
		                    </div>
                  </div>
                </div>

                <div className="poker-seat-row poker-seat-row-bottom" role="region" aria-label="Your seat">
                  {bottomSeatIds.map((id) => (
                    <React.Fragment key={`bottom-seat-${id}`}>{renderSeatCard(id)}</React.Fragment>
                  ))}
                </div>
              </div>

	            </div>
	          </div>

        {isGameOver ? (
          <div className="engine-board engine-score-board p-3 mt-3">
            <div className="engine-zone-title mb-2">Scoring Summary</div>
            <div className="engine-score-teams">
              {Object.entries(state.meta.scores)
                .sort(([a], [b]) => {
                  const ia = teamOrder.indexOf(a);
                  const ib = teamOrder.indexOf(b);
                  if (ia >= 0 && ib >= 0) return ia - ib;
                  if (ia >= 0) return -1;
                  if (ib >= 0) return 1;
                  return a.localeCompare(b);
                })
                .map(([teamId, score]) => (
                  <div key={teamId} className="engine-score-team">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                      <span className="badge text-bg-warning text-dark">{teamLabelById[teamId] ?? teamId}</span>
                      <span className="badge text-bg-light text-dark">{score.total} pts</span>
                    </div>

                    {score.items.length ? (
                      <div className="d-flex flex-column gap-2">
                        {score.items.map((item, idx) => (
                          <div key={`${teamId}-${item.ruleId}-${idx}`} className="engine-score-item">
                            <div className="small">
                              {item.label} ({item.points} pts)
                            </div>
                            {item.cards?.length ? (
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {item.cards.map((card) => (
                                  <span key={`${item.ruleId}-${card.id}`} className="badge text-bg-secondary">{compactCard(card)}</span>
                                ))}
                              </div>
                            ) : null}
                            {item.counts ? (
                              <div className="small mt-1">
                                {Object.entries(item.counts).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </div>
                            ) : null}
                            {item.dependency ? <div className="small mt-1">{item.dependency}</div> : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="small">No scoring cards.</div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default EngineGame;
